import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ResourceMatcherColumn, ResourceMatcherResource } from '@app/apps/bs-app-launch-form/bs-app-launch-form-controls/bs-app-launch-resource-matcher-control/resource-matcher.control';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { SearchQueryService } from '@app/core/services/search/resource-search/search-query.service';
import { SearchDatasourceFactoryService } from '@app/search/datasource/search-datasource-factory.service';
import { ISearchDataSourceOptions } from '@app/search/datasource/search.datasource';
import { GridOptions, IChooserServerSideDataParams, NgxSmartModalService } from '@bssh/comp-lib';
import { BaseModalComponent, ModalComponentType } from '../base-modal/base-modal.component';
import { ResourceMatcherGridOptionsFactoryService } from './gridOptions/resource-matcher-grid-options-factory.service';
import { IResourceMatcherModalInput, IResourceMatcherModalOutput } from '../model/action-modal';
import { interval } from 'rxjs';

interface PairingTableRow {
    rowIndex: number;
    columns: {
        [columnId: string]: ResourceMatcherResource | null;
    };
}

@Component({
  selector: 'app-resource-matcher-modal',
  templateUrl: './resource-matcher-modal.component.html',
  styleUrls: ['./resource-matcher-modal.component.scss']
})
export class ResourceMatcherModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
  modalComponentType = ModalComponentType.RAD;
  closable: boolean = true;
  modalType = 'resourceMatcherModal';
  
  // Configuration
  columns: ResourceMatcherColumn[] = [];
  maxRows: number = 100;
  allowUnpairedCells: boolean = false;
  allowRepetition: boolean = false;
  searchQuery: string = '';
  currentUserId: string = '';
  searchDataSourceOptions: ISearchDataSourceOptions = null;

  // Resource chooser configuration
  public resourceType: SearchResourceType;
  public resourceDictionary: ISearchResourceConfig;
  public chooserServerSideParams: IChooserServerSideDataParams;
  public gridOptions: GridOptions;
  public searchPlaceHolderText = 'Search';
  public tableId: string = '';
  
  // Pairing table data
  pairingRows: PairingTableRow[] = [];
  selectedCellKey: string | null = null;
  
  // Track which column is currently being populated
  activeColumnIndex: number = 0;

  // Track which slot is being hovered over during drag
  hoveredSlotKey: string | null = null;

  synchronousErrors: string[] = [];

  constructor(
    public ngxSmartModalService: NgxSmartModalService,
    private searchDatasourceFactory: SearchDatasourceFactoryService,
    private resourceMatcherGridOptionsFactory: ResourceMatcherGridOptionsFactoryService,
    private searchResourceDictionaryService: SearchResourceDictionaryService,
    private searchQueryService: SearchQueryService
  ) {
    super(ngxSmartModalService);
  }

  ngOnInit() {
    super.ngOnInit();

    this.subs.sink = this.data.subscribe((data: IResourceMatcherModalInput) => {
      if (data) {
        this.resourceType = data.resourceType;
        this.columns = JSON.parse(JSON.stringify(data.columns)) as any as ResourceMatcherColumn[];
        this.maxRows = data.maxRows;
        this.allowUnpairedCells = data.allowUnpairedCells;
        this.allowRepetition = data.allowRepetition;
        this.currentUserId = data.currentUserId;
        this.searchDataSourceOptions = data.searchDataSourceOptions;
        this.tableId = data.tableId;

        // Initialize pairing table from existing columns data
        this.initializePairingTable();

        // Set up resource chooser
        this.setupResourceChooser(data.searchDataSourceOptions);
      }
    });

  }

  private refreshPairingRows(): void {
    const maxPairingRowsLength = Math.max(...this.columns.map(col => col.resources ? col.resources.length : 0)) + 1;
    const pairingRowsLength = this.maxRows > maxPairingRowsLength ? maxPairingRowsLength : this.maxRows;

    this.pairingRows = [];
    for (let i = 0; i < pairingRowsLength; i++) {
      const rowColumns: { [columnId: string]: ResourceMatcherResource | null } = {};
      
      this.columns.forEach(column => {
        rowColumns[column.id] = column.resources && column.resources[i] 
          ? column.resources[i] 
          : null;
      });
      
      this.pairingRows.push({
        rowIndex: i,
        columns: rowColumns
      });
    }

    if (this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.redrawRows();
    }

    this.synchronousErrors = this.getSynchronousErrors();
  }

  private initializePairingTable(): void {
    this.refreshPairingRows();
  }

  private isMouseOverElement(element: HTMLElement | null, mouseX: number, mouseY: number): boolean {
    if (!element) return false;
    
    const rect = element.getBoundingClientRect();
    return (
      mouseX >= rect.left &&
      mouseX <= rect.right &&
      mouseY >= rect.top &&
      mouseY <= rect.bottom
    );
  }

  isSlotHovered(rowIndex: number, columnId: string): boolean {
    return this.hoveredSlotKey === `${rowIndex}-${columnId}`;
  }

  private detectHoveredSlot(mouseX: number, mouseY: number): { rowIndex: number; columnId: string } | null {
    // Get all slot elements
    const slotElements = document.querySelectorAll('[data-slot-row][data-slot-column]');
    
    for (let i = 0; i < slotElements.length; i++) {
      const element = slotElements[i] as HTMLElement;
      if (this.isMouseOverElement(element, mouseX, mouseY)) {
        const rowIndex = parseInt(element.getAttribute('data-slot-row') || '0', 10);
        const columnId = element.getAttribute('data-slot-column') || '';
        
        if (columnId) {
          return { rowIndex, columnId };
        }
      }
    }
    
    return null;
  }

  private setupResourceChooser(searchDataSourceOptions: ISearchDataSourceOptions): void {
    // Track dragging state outside grid options scope
    let isDragging = false;
    let draggedRowData: any = null;
    let globalMouseMoveHandler: ((event: MouseEvent) => void) | null = null;
    let globalMouseUpHandler: ((event: MouseEvent) => void) | null = null;

    const cleanup = () => {
      // Reset hovered slot
      this.hoveredSlotKey = null;

      if (globalMouseMoveHandler) {
        document.removeEventListener('mousemove', globalMouseMoveHandler);
        globalMouseMoveHandler = null;
      }
      
      if (globalMouseUpHandler) {
        document.removeEventListener('mouseup', globalMouseUpHandler);
        globalMouseUpHandler = null;
      }
      
      draggedRowData = null;
      isDragging = false;
    };

    this.resourceDictionary = this.searchResourceDictionaryService.getResourceConfigByType(this.resourceType);

    // Set up the search datasource and grid options for this resource type
    const searchDataSource = this.searchDatasourceFactory.getSearchDatasourceForResource(
      this.resourceType, 
      { ...searchDataSourceOptions }
    );

    this.chooserServerSideParams = {
      serverSideRequestOptions: searchDataSource.requestOptions,
      rawTextFilterProcessing: searchDataSource.rawTextFilterProcessing,
    };

    const searchGridOptions = this.resourceMatcherGridOptionsFactory.getGridOptionsFormatterForResource(this.resourceType);
    
    // Set up column definitions and enable row dragging on the first column
    const columnDefinitions = searchGridOptions.getColumnDefinitions();

    if (columnDefinitions && columnDefinitions.length > 0) {
      columnDefinitions[0] = {
        ...columnDefinitions[0],
        rowDrag: this.canDragRow
      };
    }
    
    this.gridOptions = searchGridOptions.getGridOptions({
        columnDefs: columnDefinitions,
        rowDragManaged: false,
        animateRows: true,
        rowSelection: undefined,
        rowHeight: 36,
        suppressRowClickSelection: true,
        suppressRowHoverHighlight: true,
        suppressRowDrag: false,
        suppressHorizontalScroll: false,
        icons: {
          rowDrag: `<span class="ag-icon ag-icon-grip" style="cursor: move;"></span>`  // Custom drag icon
        },
        onRowDragEnter: (event) => {
          // Only set up once per drag operation
          if (isDragging) return;
          
          isDragging = true;
          draggedRowData = event.node.data;

          // Set up global mouseup listener to catch drops outside grid
          globalMouseUpHandler = (mouseEvent: MouseEvent) => {
            if (!isDragging) return;
            
            isDragging = false;
            
            // Detect which specific dropzone was dropped into
            const hoveredSlot = this.detectHoveredSlot(mouseEvent.clientX, mouseEvent.clientY);
            
            if (draggedRowData) {
              // Successfully dropped in a valid zone
              this.handleRowDrop({ node: { data: draggedRowData } }, hoveredSlot);
            }

            cleanup();
          };

          globalMouseMoveHandler = (mouseEvent: MouseEvent) => {
            if (!isDragging) return;

            // Detect and highlight specific slot being hovered
            const hoveredSlot = this.detectHoveredSlot(mouseEvent.clientX, mouseEvent.clientY);
            if (hoveredSlot) {
              this.hoveredSlotKey = `${hoveredSlot.rowIndex}-${hoveredSlot.columnId}`;
            } else {
              this.hoveredSlotKey = null;
            }
          }
          
          document.addEventListener('mousemove', globalMouseMoveHandler);
          document.addEventListener('mouseup', globalMouseUpHandler);
        },
        isRowSelectable: (node) => {
          return !this.isRowDisabled(node);
        },
        getRowClass: (params) => {
          return !this.isRowDisabled(params.node) ? '' : 'row-unselectable';
      },
    })
  }

  private isResourceAlreadyUsed(resourceId: string): boolean {
    // Check all columns to see if this resource ID is already placed
    for (const column of this.columns) {
      if (column.resources && column.resources.some(resource => resource && String(resource.id) === resourceId)) {
        return true;
      }
    }
    return false;
  }

  public canDragRow = (params: any): boolean => {
    // Don't allow dragging if no data
    if (!params.data) {
      return false;
    }
  
    // Get the resource from the search result
    const searchResultItem = params.data;
    const resource = this.searchResourceDictionaryService.getSearchResultResource(
      searchResultItem, 
      this.resourceType
    );
  
    // Check if this resource is already used in any column
    if (!resource || !resource.Id) {
      return false;
    }
  
    // Only allow dragging if the resource hasn't been added yet
    return !this.isResourceAlreadyUsed(resource.Id);
  }

  public isRowDisabled = (params: any): boolean => {
    if (!params.data) {
      return false;
    }
  
    const searchResultItem = params.data;
    const resource = this.searchResourceDictionaryService.getSearchResultResource(
      searchResultItem, 
      this.resourceType
    );
  
    if (!resource || !resource.Id) {
      return false;
    }
  
    return this.isResourceAlreadyUsed(resource.Id);
  }

  private handleRowDrop(params: any, hoveredSlot: { columnId: string, rowIndex: number }): void {
    // Extract the dragged row data
    const draggedNode = params.node;
    if (!draggedNode || !draggedNode.data) {
      return;
    }

    const searchResultItem = draggedNode.data;
    const resource = this.searchResourceDictionaryService.getSearchResultResource(
      searchResultItem, 
      this.resourceType
    );
    
    let resourceName = this.searchResourceDictionaryService.getSearchResourceName(
      searchResultItem,
      this.resourceType
    );

    if(this.resourceType === SearchResourceType.BiosampleLibraryPrep) {
      resourceName = `${resourceName}::${searchResultItem.BioSampleLibraryPrep.PrepKitName}`;
    }
    
    const resourceData: ResourceMatcherResource = {
      id: resource.Id,
      name: resourceName,
      type: this.resourceType as any
    };
    
    // Add to the appropriate column
    if(hoveredSlot) {
      this.addResourceToSlot(resourceData, hoveredSlot);
    }
    
  }

  addResourceToSlot(resource: ResourceMatcherResource, hoveredSlot: { columnId: string, rowIndex: number }): void {
    const { columnId, rowIndex } = hoveredSlot;
    const column: ResourceMatcherColumn = this.columns.find((column) => column.id === columnId);

    column.resources[rowIndex] = resource;

    const maxColumnResourceLength = Math.max(...this.columns.map(col => col.resources ? col.resources.length : 0))
    this.columns.forEach((column) => {
      new Array(maxColumnResourceLength).fill(null).forEach((_, index) => {
        if(!column.resources[index]) {
          column.resources[index] = null;
        }
      })
    })

    this.refreshPairingRows();
  }

  removeResourceFromSlot(rowIndex: number, columnId: string): void {
    const column: ResourceMatcherColumn = this.columns.find((column) => column.id === columnId);
    column.resources[rowIndex] = null;

    this.refreshPairingRows();
  }

  confirmSelection(): void {
    // Build columns from pairing table
    const updatedColumns: ResourceMatcherColumn[] = this.columns.map(column => ({
      ...column,
      resources: []
    }));

    const filteredPairs = this.pairingRows.filter((row) => !this.isRowEmpty(row));

    filteredPairs.forEach(row => {
      updatedColumns.forEach(column => {
        const resource = row.columns[column.id];
        if (resource) {
          column.resources.push(resource);
        } else if (this.allowUnpairedCells) {
          column.resources.push(null);
        }
      });
    });

    const output: IResourceMatcherModalOutput = {
      columns: updatedColumns as any
    };

    this.confirm.emit(output);
    this.modal.close();
  }

  isRowFull(row: PairingTableRow): boolean {
    return Object.values(row.columns).every(r => r !== null);
  }

  isRowEmpty(row: PairingTableRow): boolean {
    return Object.values(row.columns).every(r => r == null);
  }

  isRowPartiallyFull(row: PairingTableRow): boolean {
    return !this.isRowEmpty(row) && !this.isRowFull(row);
  }

  getSynchronousErrors(): string[] {
    const synchronousErrors = [];
    if(!this.allowUnpairedCells) {
      this.pairingRows.forEach((row, index) => {
        if(this.isRowPartiallyFull(row)) {
          synchronousErrors.push(`You have an empty cell at row ${index + 1}.`)
        }
      })
    }

    return synchronousErrors;
  }

  get canConfirm(): boolean {
      if(this.allowUnpairedCells) {
        return true;
      } else {
        const filteredPairs = this.pairingRows.filter((row) => !this.isRowEmpty(row));
        return filteredPairs.every((row) => this.isRowFull(row));
      }
  }

  ngOnDestroy() {
    if ((this as any)._dragCleanup) {
      (this as any)._dragCleanup();
    }
    super.ngOnDestroy();
  }
}

